import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import {
  createStyles,
  DialogActions,
  DialogContent,
  DialogContentText,
  Theme,
  WithStyles,
  withStyles,
} from '@material-ui/core';
import classnames from 'classnames';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useCurrentCountry } from '@lib/hooks';
import { Country } from '@lib/providers/LanguageProvider';

import {
  validationSchemaPatientContactEmail,
  validationSchemaPatientContactSms,
  validationSchemaPatientContactWhatsapp,
} from 'utils/validation';

import { Button } from './Button';
import { Dialog, DialogProps } from './Dialog';
import DialogTitle from './DialogTitle2';
import { Typography } from './Typography';
import UserContactFieldSelect, { UserContactFieldSelectProps } from './UserContactFieldSelect';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {},
    form: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    inputAdornment: {
      margin: unit,
    },
    inputAdornmentButton: {
      '&:hover': {
        backgroundColor: 'initial',
      },
    },
    marginLeft: {
      marginLeft: unit,
    },
    textFieldWrapper: {
      minHeight: 80,
    },
  });

const messages = defineMessages({
  title: {
    id: 'components.v2.DialogPatientContact.title',
    defaultMessage: 'Create one time contact',
  },
  header: {
    id: 'components.v2.DialogPatientContact.header',
    defaultMessage: 'Contact information',
  },
  description: {
    id: 'components.v2.DialogPatientContact.description',
    defaultMessage:
      'Note: This message will be sent to this contact only once. This contact will not be saved in the patient’s profile.',
  },
  cancel: {
    id: 'components.v2.DialogPatientContact.cancel',
    defaultMessage: 'Cancel',
  },
  submit: {
    id: 'components.v2.DialogPatientContact.submit',
    defaultMessage: 'Create',
  },
});

const validationSchema = Yup.object().shape({
  value: Yup.string()
    .required('Required')
    .when('type', (type: 'sms' | 'email' | 'whatsapp', schema: Yup.StringSchema<any>) => {
      if (type === 'email') {
        return schema.concat(validationSchemaPatientContactEmail);
      }
      if (type === 'whatsapp') {
        return schema.concat(validationSchemaPatientContactWhatsapp);
      }
      return schema.concat(validationSchemaPatientContactSms);
    }),
});

export type PatientContactTemp = { type: string; value: string };

export interface DialogPatientContactProps
  extends Omit<DialogProps, 'classes' | 'onClose' | 'onSubmit'>,
    WithStyles<typeof styles> {
  initialValues?: PatientContactTemp;
  onClose: () => void;
  onSubmit: (patientContactTemp: PatientContactTemp) => void;
}

const DialogPatientContact: React.FC<DialogPatientContactProps> = ({
  classes,
  className,
  initialValues = { type: 'sms', value: '' },
  onClose,
  onSubmit,
  ...rest
}) => {
  const { formatMessage } = useIntl();

  const country = useCurrentCountry();
  const typeOptions = useMemo(() => {
    const res: UserContactFieldSelectProps['typeOptions'] = ['sms', 'email'];
    if (country === Country.Brazil) {
      res.push('whatsapp');
    }
    return res;
  }, [country]);

  return (
    <Dialog className={classnames(classes.root, className)} onClose={onClose} {...rest}>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        // TODO use Formik default `onSubmit` to skip propagation to parent
        // https://github.com/formium/formik/pull/1515
        onSubmit={() => {}}
      >
        {({ values, setFieldValue, errors, touched, isValid, setFieldTouched }) => (
          <Form
            className={classes.form}
            // WORKAROUND prevent enter key propagation to parent
            // This shouldn't be necessary after Formik upgrade
            // https://stackoverflow.com/a/56075247/12993819
            // https://app.clickup.com/t/337hk7
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          >
            <DialogContent>
              <Typography variant="h6" paragraph>
                {formatMessage(messages.header)}
              </Typography>
              <div className={classes.textFieldWrapper}>
                <UserContactFieldSelect
                  value={values}
                  typeOptions={typeOptions}
                  onChangeContactType={(type: any) => setFieldValue('type', type)}
                  onChangeContactValue={(e: any) => setFieldValue('value', e.target.value)}
                  onBlur={() => setFieldTouched('value', true)}
                  error={Boolean(touched.value && errors.value)}
                  helperText={touched.value && errors.value}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  autoFocus={!values.value}
                  onFocus={(event) => event.target.select()}
                />
              </div>
              <DialogContentText>{formatMessage(messages.description)}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={onClose}>
                {formatMessage(messages.cancel)}
              </Button>
              <Button variant="contained" color="primary" disabled={!isValid} onClick={() => onSubmit(values)}>
                {formatMessage(messages.submit)}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default withStyles(styles, { name: 'LumaDialogPatientContact' })(DialogPatientContact);
